import { useCallback } from "react";

import { useMutation } from "@apollo/client";
import { message } from "antd";

import { renderErrorMessage } from "Services/LoggingService";
import { CommunicationTemplateDTO } from "Types/CommunicationTemplate";

import { createCommunicationTemplateGql, updateCommunicationTemplateGql } from "./mutations";

interface HookProps {
    id?: string;
}

export function useCommunicationTemplateMutations({ id }: HookProps) {
    const isEditMode = Boolean(id);

    const [createCommunicationTemplateMutation, { loading: isCreating }] = useMutation(createCommunicationTemplateGql);
    const [updateCommunicationTemplateMutation, { loading: isUpdating }] = useMutation(updateCommunicationTemplateGql);

    const errorHandler = useCallback((error: unknown, message: string) => {
        renderErrorMessage({
            content: message,
            error: error instanceof Error ? error : new Error(String(error)),
        });
    }, []);

    const createCommunicationTemplate = useCallback(
        async (formData: CommunicationTemplateDTO, usePreview: boolean) => {
            try {
                const result = await createCommunicationTemplateMutation({
                    variables: {
                        createCommunicationTemplateInput: formData,
                        usePreview,
                    },
                });
                message.success("Successfully created Template");
                return result.data;
            } catch (error) {
                errorHandler(error, "Templated failed to be created.");
            }
        },
        [createCommunicationTemplateMutation, errorHandler]
    );

    const updateCommunicationTemplate = useCallback(
        async (formData: CommunicationTemplateDTO, usePreview: boolean) => {
            try {
                const result = await updateCommunicationTemplateMutation({
                    variables: {
                        updateCommunicationTemplateInput: formData,
                        usePreview,
                    },
                });
                message.success("Successfully updated Template");
                return result.data;
            } catch (error) {
                errorHandler(error, "Templated failed to be updated.");
            }
        },
        [updateCommunicationTemplateMutation, errorHandler]
    );

    return {
        isEditMode,
        isMutatingData: isCreating || isUpdating,
        createCommunicationTemplate,
        updateCommunicationTemplate,
    };
}
